import DECORATION_TYPE from "../../constants/decorationTypes";

export const RIVERR_FEE_PCT = 15;
export const PROCESSING_FEE_PCT = 2.9;

export function dollarsToCents(value) {
  if (value === "" || value == null) return null;
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

export function isFreeSellerPlan(plan) {
  if (!plan) return false;
  const n = Number(plan.priceCents);
  return !Number.isFinite(n) || n <= 0;
}

export function formatMoney(cents) {
  if (cents == null) return "0";
  const n = cents / 100;
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

export function priceChangeTitle(change) {
  if (!change) return "";
  const name = change.planName || "your plan";
  if (change.acceptByLabel) {
    return `Accept the new ${name} price by ${change.acceptByLabel}`;
  }
  return `${name} price update`;
}

/** Seller-facing warning: keep old price until accept; miss deadline → Free. */
export function priceChangeWarningCopy(change) {
  if (!change) return "";
  const name = change.planName || "Your plan";
  const oldP = formatMoney(change.billedPriceCents);
  const newP = formatMoney(change.newPriceCents);
  const now = `${name} is now $${newP}/mo.`;
  const keepOld = `You're still on the old $${oldP}/mo until you accept.`;
  if (change.acceptByLabel) {
    return `${now} ${keepOld} If you don't accept by ${change.acceptByLabel}, you'll be switched to Free.`;
  }
  return `${now} ${keepOld}`;
}

export function netCents(priceCents) {
  if (!priceCents) return 0;
  return Math.round(
    priceCents * (1 - (RIVERR_FEE_PCT + PROCESSING_FEE_PCT) / 100)
  );
}

export function storesLabel(maxStores) {
  return maxStores == null || maxStores === "" ? "Unlimited" : String(maxStores);
}

export function annualMonthlyCents(annualPriceCents) {
  if (!annualPriceCents) return null;
  return Math.round(annualPriceCents / 12);
}

export function annualSavePct(monthlyCents, annualPriceCents) {
  if (!monthlyCents || !annualPriceCents) return null;
  const yearAtMonthly = monthlyCents * 12;
  if (yearAtMonthly <= annualPriceCents) return null;
  return Math.round((1 - annualPriceCents / yearAtMonthly) * 100);
}

export function splitCorePlans(plans = []) {
  const sorted = [...plans].sort(
    (a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99)
  );
  const free = sorted.find((p) => p.isDefault || !p.priceCents) || null;
  const premium =
    sorted.find(
      (p) =>
        p.id !== free?.id &&
        (p.popular || (p.priceCents || 0) > 0)
    ) || null;
  const coreIds = new Set([free?.id, premium?.id].filter(Boolean));
  const extra = sorted.filter((p) => !coreIds.has(p.id));
  return { free, premium, extra, core: [free, premium].filter(Boolean) };
}

export function catalogLimitLabel(plan) {
  if (plan?.catalogScope !== "categories") return "Full catalog";
  const n = (plan.allowedCatalogCategories || []).length;
  return n ? `${n} categories` : "None selected";
}

export function decorationLimitLabel(plan) {
  if (plan?.decorationScope !== "methods") return "All methods";
  const labels = (plan.allowedDecorationMethodIds || [])
    .map((id) => DECORATION_TYPE[id] || DECORATION_TYPE[String(id)])
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "None selected";
}

export function designLimitLabel(plan) {
  if (plan?.designSource === "templates" || plan?.uploadLimit === 0) {
    return "Templates only";
  }
  if (plan?.uploadLimit == null) return "Unlimited uploads";
  const period = plan.uploadPeriod === "lifetime" ? "all time" : "per month";
  return `${plan.uploadLimit} uploads ${period}`;
}

export function logoLocationLabel(plan) {
  const logos =
    plan?.maxLogos == null
      ? "Unlimited logos"
      : `${plan.maxLogos} logo${plan.maxLogos === 1 ? "" : "s"}`;
  const locations =
    plan?.maxLocations == null
      ? "Unlimited locations"
      : `${plan.maxLocations} location${plan.maxLocations === 1 ? "" : "s"}`;
  return `${logos}, ${locations}`;
}

export function featureLabels(plan) {
  return (plan.features || [])
    .filter((f) => f.included !== false && (f.label || f).toString().trim())
    .map((f) => (typeof f === "string" ? f : f.label));
}

/** Axios (or already-unwrapped) list responses. */
export function asList(res) {
  const body = res?.data !== undefined ? res.data : res;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.plans)) return body.plans;
  return [];
}

export function planCatalogTierIds(plan) {
  const fromArray = Array.isArray(plan?.catalogTierIds)
    ? plan.catalogTierIds
    : [];
  const legacy = plan?.catalogTierId ? [plan.catalogTierId] : [];
  return [...new Set([...fromArray, ...legacy].map(String).filter(Boolean))];
}

export function pricingDiscountLabel(discount) {
  const n = Number(discount);
  if (!Number.isFinite(n) || n === 0) return null;
  return n > 0 ? `${n}% off` : `+${Math.abs(n)}%`;
}

export function tierOptionLabel(tier) {
  if (!tier) return "";
  const name = tier.name || "Untitled tier";
  if (tier.discount == null || tier.discount === "") return name;
  const n = Number(tier.discount);
  if (!Number.isFinite(n) || n === 0) return `${name} (0%)`;
  return n > 0 ? `${name} (${n}% off)` : `${name} (+${Math.abs(n)}%)`;
}
